import React, { useEffect, useMemo, useState, useCallback } from "react"
import Icon from "../../../components/AppIcon"
import { Card, CardContent } from "../../../components/ui/card"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../../components/ui/table"
import { listCourses } from "../../../utils/courseHelpers"
import { computeCoursePerformance } from "../../../utils/coursePerformanceHelpers"
import { computeCourseAnalytics } from "../../../utils/courseDetailHelpers"
import { listOrgProductViews } from "../../../utils/productViewHelpers"
import { computeProductConversion } from "../../../utils/productConversionHelpers"

const formatCurrency = (n) => `$${(n || 0).toLocaleString()}`

const FunnelStage = ({ stage, isFirst }) => (
  <div className="flex items-center gap-3">
    {!isFirst && <Icon name="ChevronRight" size={14} className="text-muted-foreground shrink-0" />}
    <div className="min-w-[110px]">
      <p className="text-xs text-muted-foreground">{stage.label}</p>
      <p className="text-lg font-bold text-foreground">{stage.value.toLocaleString()}</p>
      {stage.stepConversion !== null && (
        <p className="text-[11px] text-muted-foreground">{stage.stepConversion}% of prior</p>
      )}
    </div>
  </div>
)

const IntelligenceCoursesPanel = ({ organization }) => {
  const orgId = organization?.orgId
  const [courses, setCourses] = useState([])
  const [stats, setStats] = useState({})
  const [viewsByCourse, setViewsByCourse] = useState({})
  const [funnel, setFunnel] = useState(null)
  const [loading, setLoading] = useState(true)

  const [selectedCourseId, setSelectedCourseId] = useState(null)
  const [detail, setDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)

  useEffect(() => {
    if (!orgId) return
    const load = async () => {
      setLoading(true)
      const { courses: courseList } = await listCourses(orgId)
      const [perf, { views }, conversion] = await Promise.all([
        computeCoursePerformance(orgId, courseList || []),
        listOrgProductViews(orgId),
        computeProductConversion(orgId, "30d"),
      ])

      const viewCounts = {}
      views?.forEach((v) => {
        if (!v?.productId) return
        viewCounts[v.productId] = (viewCounts[v.productId] || 0) + 1
      })

      setCourses(courseList || [])
      setStats(perf)
      setViewsByCourse(viewCounts)
      setFunnel(conversion)
      setLoading(false)
    }
    load()
  }, [orgId])

  const rows = useMemo(
    () =>
      courses
        .map((c) => ({
          course: c,
          views: viewsByCourse[c.courseId] || 0,
          ...(stats[c.courseId] || { learners: 0, completion: 0, revenue: 0 }),
        }))
        .sort((a, b) => b.learners - a.learners),
    [courses, stats, viewsByCourse]
  )

  const openCourse = useCallback(
    async (course) => {
      setSelectedCourseId(course.courseId)
      setDetailLoading(true)
      const analytics = await computeCourseAnalytics(orgId, course)
      setDetail(analytics)
      setDetailLoading(false)
    },
    [orgId]
  )

  const selectedCourse = courses.find((c) => c.courseId === selectedCourseId) || null

  if (loading) {
    return <div className="p-12 text-center text-muted-foreground text-sm">Loading course intelligence...</div>
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground max-w-xl">
        How each course performs — from first view through to a completed credential.
      </p>

      {/* Conversion funnel */}
      {funnel?.hasPaidProducts && (
        <Card className="dark:bg-transparent">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-sm text-foreground">Course Conversion Funnel</h3>
              <span className="text-xs text-muted-foreground">Paid courses · Last 30 days</span>
            </div>
            <div className="flex items-center gap-3 overflow-x-auto pb-2">
              {funnel.stages.map((stage, i) => (
                <FunnelStage key={stage.key} stage={stage} isFirst={i === 0} />
              ))}
            </div>
            {!funnel.visitorsTracked && (
              <p className="text-xs text-muted-foreground mt-3">
                Visitor tracking isn't recording page views yet — funnel starts from Product Views instead.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
        {/* Course table */}
        <Card className="p-0 dark:bg-transparent overflow-x-auto">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-5">Course</TableHead>
                  <TableHead className="text-right">Views</TableHead>
                  <TableHead className="text-right">Learners</TableHead>
                  <TableHead className="text-right">Completion</TableHead>
                  <TableHead className="text-right pr-5">Revenue</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.course.courseId}
                    className={`cursor-pointer hover:bg-muted/50 ${
                      selectedCourseId === row.course.courseId ? "bg-primary/5" : ""
                    }`}
                    onClick={() => openCourse(row.course)}
                  >
                    <TableCell className="pl-5 font-medium max-w-[220px] truncate">
                      {row.course.title || "Untitled Course"}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">{row.views}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{row.learners}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{row.completion}%</TableCell>
                    <TableCell className="text-right pr-5 text-muted-foreground">
                      {formatCurrency(row.revenue)}
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                      No published courses yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Drill-down detail */}
        <div>
          {!selectedCourse ? (
            <Card className="p-8 text-center dark:bg-transparent h-full flex flex-col items-center justify-center">
              <Icon name="BarChart3" size={26} className="text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">Select a course to see assessment performance.</p>
            </Card>
          ) : detailLoading ? (
            <Card className="p-8 text-center dark:bg-transparent">
              <p className="text-sm text-muted-foreground">Loading {selectedCourse.title}...</p>
            </Card>
          ) : (
            <Card className="dark:bg-transparent">
              <CardContent className="p-4 space-y-4">
                <div>
                  <p className="text-xs text-muted-foreground">Course</p>
                  <h3 className="font-semibold text-foreground">{selectedCourse.title}</h3>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Learners</p>
                    <p className="text-lg font-bold text-foreground">{detail?.totalLearners ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Avg. Completion</p>
                    <p className="text-lg font-bold text-foreground">{detail?.avgCompletion ?? 0}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Avg. Quiz Score</p>
                    <p className="text-lg font-bold text-foreground">
                      {detail?.quizAttemptCount ? `${detail.avgQuizScore}%` : "—"}
                    </p>
                    {!detail?.quizAttemptCount && (
                      <p className="text-[11px] text-muted-foreground">No quiz attempts yet</p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Capstone Pass Rate</p>
                    <p className="text-lg font-bold text-foreground">{detail?.capstonePassRate ?? 0}%</p>
                  </div>
                </div>
                <div className="pt-3 border-t border-border">
                  <p className="text-xs text-muted-foreground">Revenue</p>
                  <p className="text-lg font-bold text-foreground">{formatCurrency(detail?.revenue)}</p>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Matched by product title, courses sharing a title with another product may over/undercount.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}

export default IntelligenceCoursesPanel